<script setup lang="ts">
import axios from 'axios'

import { VueDraggableNext as draggable } from 'vue-draggable-next'
import EmailEditorPanel from './EmailEditorPanel.vue'
import EmailBlockActionMenu from './EmailBlockActionMenu.vue'
import EmailTiptapToolbarWrapper from './tiptap/EmailTiptapToolbarWrapper.vue'
import EmailVariableInput from './EmailVariableInput.vue'

import {
  EmailHeaderBlock,
  EmailTextBlock,
  EmailSpacerBlock,
  EmailImageBlock,
  EmailButtonBlock,
  EmailSeparatorBlock,
  EmailGalleryBlock,
  EmailFooterBlock,
  EmailSocialBlock
} from './blocks'
import { EmailEditorProvider } from '@/components/base/Email'
import { createEmptyTemplate, cloneTemplate } from '../templates'
import { toApiPayload, fromApiResponse, defaultEmailStyles } from '../utils/apiTransformers'
import { applyImageMask, getImageDimensions, isAnimatedImage } from '../utils/imageMask'
import { emailVariables, getVariableOptions } from '../constants/variables'
import type { EmailStyles } from '../utils/apiTransformers'

import type {
  EmailBlock,
  EmailHeader,
  FooterBlock,
  GalleryBlock,
  ImageBlock,
  ImageMaskShape,
  SocialBlock
} from '../types'

const props = defineProps<{
  templateId?: string | null
}>()

const emit = defineEmits<{
  (e: 'saved', data: { templateId: string; templateName: string }): void
  (e: 'close'): void
}>()

const { t, locale } = useI18n()
const notification = useNotification()
const { merchantName, merchantLogo, user } = useLocalStorage()
const { createEmailContent, updateEmailContent, saveAsTemplate, uploadImage, sendTestEmail, getEmailContent } =
  useEmailContent()

const selectedBlockId = ref<string | null>(null)
const selectedBlock = computed<EmailBlock | null>(() => {
  if (!selectedBlockId.value) return null
  return blocks.value.find((b) => b.id === selectedBlockId.value) ?? null
})
const selectedCellIndex = ref<number | null>(null)
const selectedCell = computed(() => {
  if (selectedCellIndex.value === null) return null
  if (selectedBlock.value?.type !== 'gallery') return null
  return selectedBlock.value.items[selectedCellIndex.value] ?? null
})
const focusedBlockId = ref<string | null>(null)
const viewMode = ref<'mobile' | 'desktop'>('desktop')
const isNewTemplate = ref(false)
const internalTemplateId = ref<string | null>(props.templateId || null)
const isSaving = ref(false)
const isSendingTest = ref(false)
const showTestEmailModal = ref(false)
const testEmailAddress = ref('')
const showSaveTemplateModal = ref(false)
const saveTemplateNameInput = ref('')
const isSavingTemplate = ref(false)
const saveModalMode = ref<'email' | 'template'>('template')

const merchantPrimaryColor = ref('000000')
const merchantSecondaryColor = ref('1e293b')

const colorPresets = computed(() => {
  const presets: { color: string; label: string }[] = []
  if (merchantPrimaryColor.value) {
    presets.push({ color: merchantPrimaryColor.value, label: t('pages.email.emailTemplateCreatePage.primaryColor') })
  }
  if (merchantSecondaryColor.value) {
    presets.push({
      color: merchantSecondaryColor.value,
      label: t('pages.email.emailTemplateCreatePage.secondaryColor')
    })
  }
  return presets
})

const saveTemplate = () => {
  saveTemplateNameInput.value = templateName.value
  saveModalMode.value = 'template'
  showSaveTemplateModal.value = true
}

const handleSaveModalConfirm = async () => {
  if (saveModalMode.value === 'email') {
    templateName.value = saveTemplateNameInput.value.trim()
    showSaveTemplateModal.value = false
    handleSave()
    return
  }

  if (isSavingTemplate.value) return

  const name = saveTemplateNameInput.value.trim()
  if (!name) {
    notification.create('error', {
      text: t('pages.email.emailTemplateCreatePage.nameRequired')
    })
    return
  }

  isSavingTemplate.value = true

  try {
    const payload = toApiPayload(
      name,
      emailMetadata.value.subject,
      emailMetadata.value.previewText,
      emailHeader.value,
      blocks.value,
      emailStyles.value
    )

    await saveAsTemplate(payload)

    showSaveTemplateModal.value = false
    notification.create('success', {
      text: t('pages.email.emailTemplateCreatePage.templateSaved')
    })
  } catch {
    notification.create('error', {
      text: t('pages.email.emailTemplateCreatePage.saveError')
    })
  } finally {
    isSavingTemplate.value = false
  }
}

const SUBJECT_MAX_LENGTH = 60
const PREVIEW_TEXT_MAX_LENGTH = 90

const templateName = ref(t('pages.email.emailTemplateCreatePage.newTemplate'))
const emailMetadata = ref({
  from: 'contact@heypongo.link',
  subject: '',
  replyTo: '',
  previewText: ''
})

const isSubjectAtLimit = computed(() => emailMetadata.value.subject.length >= SUBJECT_MAX_LENGTH)
const isPreviewTextAtLimit = computed(() => emailMetadata.value.previewText.length >= PREVIEW_TEXT_MAX_LENGTH)

type VariableInput = { insertVariable: (text: string) => void }
const subjectFieldRef = ref<VariableInput | null>(null)
const previewTextFieldRef = ref<VariableInput | null>(null)

const activeMetadataField = ref<'subject' | 'previewText' | null>(null)

const metadataVariables = computed(() => getVariableOptions(emailVariables))

const onMetadataFieldFocus = (field: 'subject' | 'previewText') => {
  activeMetadataField.value = field
  selectedBlockId.value = null
}

const onInsertMetadataVariable = (code: string) => {
  const field = activeMetadataField.value
  if (!field) return
  const target = field === 'subject' ? subjectFieldRef.value : previewTextFieldRef.value
  target?.insertVariable(`{{${code}}}`)
}

watch(
  () => emailMetadata.value.subject,
  (value) => {
    if (value && value.length > SUBJECT_MAX_LENGTH) {
      emailMetadata.value.subject = value.slice(0, SUBJECT_MAX_LENGTH)
    }
  },
  { immediate: true }
)

watch(
  () => emailMetadata.value.previewText,
  (value) => {
    if (value && value.length > PREVIEW_TEXT_MAX_LENGTH) {
      emailMetadata.value.previewText = value.slice(0, PREVIEW_TEXT_MAX_LENGTH)
    }
  },
  { immediate: true }
)

const emailHeader = ref<EmailHeader>({
  logo: '',
  showLogo: true,
  align: 'center',
  link: ''
})

const blocks = ref<EmailBlock[]>([])

const emailStyles = ref<EmailStyles>({ ...defaultEmailStyles })

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })
}

const uploadImageToS3 = async (file: File): Promise<string | null> => {
  try {
    const base64 = await fileToBase64(file)
    const url = await uploadImage({ base64, originalName: file.name })
    if (!url) {
      throw new Error('No URL in response')
    }
    return url
  } catch (error) {
    console.error('Failed to upload image:', error)
    notification.create('error', {
      text: t('pages.email.emailTemplateCreatePage.uploadError')
    })
    return null
  }
}

const uploadingBlockIds = ref<Set<string>>(new Set())
const isHeaderUploading = ref(false)

const onHeaderUpload = async (file: File) => {
  isHeaderUploading.value = true
  try {
    const s3Url = await uploadImageToS3(file)
    if (s3Url) emailHeader.value.logo = s3Url
  } finally {
    isHeaderUploading.value = false
  }
}

/**
 * Per-image cache of the original (unmasked) File, kept in memory during
 * the editor session so the user can re-apply a different mask without
 * re-uploading. Cleared on page reload.
 */
const originalImageFiles = new Map<string, File>()
const masksBusy = ref<Set<string>>(new Set())

const onImageBlockUpload = async (blockId: string, file: File) => {
  const imageBlock = blocks.value.find((b) => b.id === blockId && b.type === 'image') as ImageBlock | undefined
  if (!imageBlock) return

  uploadingBlockIds.value.add(blockId)
  try {
    originalImageFiles.set(blockId, file)
    const currentMask = isAnimatedImage(file) ? 'none' : (imageBlock.mask ?? 'none')
    const originalUrl = await uploadImageToS3(file)
    const maskedUrl =
      currentMask === 'none' ? originalUrl : await uploadImageToS3(await applyImageMask(file, currentMask))
    if (originalUrl && maskedUrl) {
      const fresh = blocks.value.find((b) => b.id === blockId && b.type === 'image') as ImageBlock | undefined
      if (fresh) onBlockUpdate({ ...fresh, src: maskedUrl, originalSrc: originalUrl, mask: currentMask })
    }
  } catch (error) {
    console.error('Failed to apply mask:', error)
    notification.create('error', { text: t('pages.email.emailTemplateCreatePage.uploadError') })
  } finally {
    uploadingBlockIds.value.delete(blockId)
  }
}

const onImageMaskChange = async (blockId: string, mask: ImageMaskShape) => {
  const imageBlock = blocks.value.find((b) => b.id === blockId && b.type === 'image') as ImageBlock | undefined
  if (!imageBlock) return
  const previousMask = imageBlock.mask ?? 'none'
  if (previousMask === mask) return
  if (isAnimatedImage(originalImageFiles.get(blockId) ?? imageBlock.originalSrc ?? imageBlock.src)) return

  onBlockUpdate({ ...imageBlock, mask })
  if (!imageBlock.src) return

  masksBusy.value.add(blockId)
  try {
    const original = originalImageFiles.get(blockId) ?? imageBlock.originalSrc ?? imageBlock.src
    const masked = await applyImageMask(original, mask)
    const s3Url = await uploadImageToS3(masked)
    if (s3Url) {
      const fresh = blocks.value.find((b) => b.id === blockId && b.type === 'image') as ImageBlock | undefined
      if (fresh) onBlockUpdate({ ...fresh, src: s3Url, mask })
    }
  } catch (error) {
    console.error('Failed to apply mask:', error)
    notification.create('error', { text: t('pages.email.emailTemplateCreatePage.uploadError') })
    const fresh = blocks.value.find((b) => b.id === blockId && b.type === 'image') as ImageBlock | undefined
    if (fresh) onBlockUpdate({ ...fresh, mask: previousMask })
  } finally {
    masksBusy.value.delete(blockId)
  }
}

const uploadingGalleryItems = ref<Map<string, Set<number>>>(new Map())

const getGalleryUploadSet = (blockId: string): Set<number> => {
  let set = uploadingGalleryItems.value.get(blockId)
  if (!set) {
    set = new Set<number>()
    uploadingGalleryItems.value.set(blockId, set)
  }
  return set
}

const onGalleryItemUpload = async (blockId: string, payload: { index: number; file: File }) => {
  const galleryBlock = blocks.value.find((b) => b.id === blockId && b.type === 'gallery') as GalleryBlock | undefined
  if (!galleryBlock) return
  const item = galleryBlock.items[payload.index]
  if (!item || item.type !== 'image') return

  const uploads = getGalleryUploadSet(blockId)
  uploads.add(payload.index)
  uploadingGalleryItems.value = new Map(uploadingGalleryItems.value)
  try {
    const [s3Url, dimensions] = await Promise.all([
      uploadImageToS3(payload.file),
      getImageDimensions(payload.file).catch(() => null)
    ])
    if (s3Url) {
      const items = galleryBlock.items.slice()
      const { naturalWidth: _width, naturalHeight: _height, ...itemWithoutDimensions } = item
      items[payload.index] = dimensions
        ? { ...itemWithoutDimensions, src: s3Url, naturalWidth: dimensions.width, naturalHeight: dimensions.height }
        : { ...itemWithoutDimensions, src: s3Url }
      onBlockUpdate({ ...galleryBlock, items })
    }
  } finally {
    uploads.delete(payload.index)
    uploadingGalleryItems.value = new Map(uploadingGalleryItems.value)
  }
}

const selectBlock = (blockId: string) => {
  if (blockId !== selectedBlockId.value) selectedCellIndex.value = null
  selectedBlockId.value = blockId
  activeMetadataField.value = null
}

const clearSelection = () => {
  selectedBlockId.value = null
  selectedCellIndex.value = null
  activeMetadataField.value = null
}

const onPanelBack = () => {
  if (selectedCellIndex.value !== null) {
    selectedCellIndex.value = null
    return
  }
  clearSelection()
}

const onDraggableChange = (event: { added?: { element: EmailBlock } }) => {
  if (event.added) {
    selectedCellIndex.value = null
    selectedBlockId.value = event.added.element.id
  }
}

const deleteBlock = (blockId: string) => {
  const index = blocks.value.findIndex((b) => b.id === blockId)
  if (index !== -1) {
    blocks.value.splice(index, 1)
    if (selectedBlockId.value === blockId) {
      selectedBlockId.value = null
    }
  }
}

const moveBlockUp = (blockId: string) => {
  const index = blocks.value.findIndex((b) => b.id === blockId)
  if (index > 0) {
    const block = blocks.value[index]
    blocks.value.splice(index, 1)
    blocks.value.splice(index - 1, 0, block)
  }
}

const moveBlockDown = (blockId: string) => {
  const index = blocks.value.findIndex((b) => b.id === blockId)
  const footerIndex = blocks.value.findIndex((b) => b.type === 'footer')
  const maxIndex = footerIndex !== -1 ? footerIndex - 1 : blocks.value.length - 1
  if (index !== -1 && index < maxIndex) {
    const block = blocks.value[index]
    blocks.value.splice(index, 1)
    blocks.value.splice(index + 1, 0, block)
  }
}

const duplicateBlock = (blockId: string) => {
  const index = blocks.value.findIndex((b) => b.id === blockId)
  if (index !== -1) {
    const block = blocks.value[index]
    const newId = `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    const duplicatedBlock = JSON.parse(JSON.stringify({ ...block, id: newId }))
    blocks.value.splice(index + 1, 0, duplicatedBlock)
    selectedCellIndex.value = null
    selectedBlockId.value = newId
  }
}

const canMoveUp = (blockId: string) => {
  const index = blocks.value.findIndex((b) => b.id === blockId)
  return index > 0
}

const canMoveDown = (blockId: string) => {
  const index = blocks.value.findIndex((b) => b.id === blockId)
  const footerIndex = blocks.value.findIndex((b) => b.type === 'footer')
  const maxIndex = footerIndex !== -1 ? footerIndex - 1 : blocks.value.length - 1
  return index !== -1 && index < maxIndex
}

const onBlockFocus = (blockId: string) => {
  focusedBlockId.value = blockId
}

const onBlockUpdate = (updatedBlock: EmailBlock) => {
  const index = blocks.value.findIndex((b) => b.id === updatedBlock.id)
  if (index !== -1) {
    blocks.value[index] = updatedBlock
  }
}

const applyMerchantDataToTemplate = (preserveExistingData = false) => {
  if (!preserveExistingData) {
    emailHeader.value.logo = merchantLogo.value || ''
  }

  const footerBlock = blocks.value.find((b) => b.type === 'footer') as FooterBlock | undefined
  if (footerBlock) {
    if (!preserveExistingData) {
      footerBlock.logoSrc = merchantLogo.value || ''
      const companyName = merchantName.value || t('pages.email.emailTemplateCreatePage.panel.yourCompany')
      const address = (user.value.merchant as any)?.address || ''
      const contentParagraphs = [
        {
          type: 'paragraph',
          content: [{ type: 'text', text: companyName }]
        }
      ]
      if (address) {
        contentParagraphs.push({
          type: 'paragraph',
          content: [{ type: 'text', text: address }]
        })
      }
      footerBlock.content = {
        type: 'doc',
        content: contentParagraphs
      }
    }
  }

  if (!preserveExistingData) {
    blocks.value.forEach((block) => {
      if (block.type === 'button') {
        block.backgroundColor = merchantPrimaryColor.value
      }
    })
  }
}

const loadDefaultTemplate = () => {
  const cloned = cloneTemplate(createEmptyTemplate(t))

  templateName.value = cloned.name
  emailMetadata.value = { ...cloned.metadata }
  emailHeader.value = { ...cloned.header }
  blocks.value = cloned.blocks
  emailStyles.value = { ...defaultEmailStyles }

  applyMerchantDataToTemplate()
}

const openSaveModal = () => {
  handleSave()
}

const saveErrors = ref<{ subject: boolean; previewText: boolean; image: boolean }>({
  subject: false,
  previewText: false,
  image: false
})

const validateBeforeSave = (): boolean => {
  saveErrors.value.subject = !emailMetadata.value.subject.trim()
  saveErrors.value.previewText = !emailMetadata.value.previewText.trim()
  saveErrors.value.image = blocks.value.some((block) => block.type === 'image' && !(block as ImageBlock).src)

  return !saveErrors.value.subject && !saveErrors.value.previewText && !saveErrors.value.image
}

const handleSave = async () => {
  if (isSaving.value) return

  if (!validateBeforeSave()) return

  isSaving.value = true

  try {
    const name = templateName.value.trim() || emailMetadata.value.subject || 'Email'
    const payload = toApiPayload(
      name,
      emailMetadata.value.subject,
      emailMetadata.value.previewText,
      emailHeader.value,
      blocks.value,
      emailStyles.value
    )

    if (internalTemplateId.value) {
      await updateEmailContent(internalTemplateId.value, payload)
    } else {
      const result = await createEmailContent(payload)
      internalTemplateId.value = result.id
    }

    notification.create('success', {
      text: t('pages.email.emailTemplateCreatePage.saved')
    })

    emit('saved', {
      templateId: internalTemplateId.value || '',
      templateName: templateName.value
    })
    emit('close')
  } catch (error) {
    const errors = error.response?.data?.errors
    const fieldLabels = {
      'structure.subject': t('fields.subject'),
      'structure.body': t('fields.body')
    }

    if (errors) {
      const messages = Object.keys(errors).map((field) => {
        const label = fieldLabels[field] ?? field
        return t('common.fieldRequired', { field: label })
      })

      notification.create('error', { text: messages.join('\n'), timeout: 2000 })
    } else {
      notification.create('error', { text: t('pages.email.emailTemplateCreatePage.saveError'), timeout: 2000 })
    }
  } finally {
    isSaving.value = false
  }
}

const openTestEmailModal = () => {
  testEmailAddress.value = user.value.email || ''
  showTestEmailModal.value = true
}

const handleSendTest = async () => {
  if (isSendingTest.value) return

  if (!testEmailAddress.value) {
    notification.create('error', {
      text: t('pages.email.emailTemplateCreatePage.emailRequired')
    })
    return
  }

  isSendingTest.value = true

  try {
    const payload = toApiPayload(
      templateName.value,
      emailMetadata.value.subject,
      emailMetadata.value.previewText,
      emailHeader.value,
      blocks.value,
      emailStyles.value
    )

    if (internalTemplateId.value) {
      await updateEmailContent(internalTemplateId.value, payload)
    } else {
      const result = await createEmailContent(payload)
      internalTemplateId.value = result.id
    }

    await sendTestEmail(internalTemplateId.value as string, testEmailAddress.value, locale.value)

    notification.create('success', {
      text: t('pages.email.emailTemplateCreatePage.testEmailSent')
    })
    showTestEmailModal.value = false
  } catch (error: unknown) {
    const errorCode = (error as any).response?.data?.error
    if (errorCode === 'email_settings_not_configured') {
      notification.create('error', {
        text: t('pages.email.emailTemplateCreatePage.emailSettingsNotConfigured')
      })
    } else if (errorCode === 'sender_domain_not_verified') {
      notification.create('error', {
        text: t('pages.email.emailTemplateCreatePage.senderDomainNotVerified')
      })
    } else {
      notification.create('error', {
        text: t('pages.email.emailTemplateCreatePage.sendTestError')
      })
    }
    console.error('Failed to send test email:', error)
  } finally {
    isSendingTest.value = false
  }
}

const loadExistingTemplate = async (templateIdToLoad: string) => {
  try {
    const response = await getEmailContent(templateIdToLoad)
    const parsed = fromApiResponse(response)

    templateName.value = parsed.name
    emailMetadata.value.subject = parsed.subject
    emailMetadata.value.previewText = parsed.previewText
    emailHeader.value = parsed.header
    blocks.value = parsed.blocks
    emailStyles.value = parsed.styles

    applyMerchantDataToTemplate(true)
  } catch (error) {
    console.error('Failed to load template:', error)
    notification.create('error', {
      text: t('pages.email.emailTemplateCreatePage.loadError')
    })
    isNewTemplate.value = true
    loadDefaultTemplate()
  }
}

onMounted(async () => {
  try {
    const { data } = await axios.get(`merchants/${user.value.merchant.id}/colors`)
    if (data.data?.background) {
      merchantPrimaryColor.value = data.data.background.replaceAll('#', '')
    }
    if (data.data?.button) {
      merchantSecondaryColor.value = data.data.button.replaceAll('#', '')
    }
  } catch {
    merchantPrimaryColor.value = '1e293b'
  }

  if (props.templateId) {
    isNewTemplate.value = false
    internalTemplateId.value = props.templateId
    await loadExistingTemplate(props.templateId)
  } else {
    isNewTemplate.value = true
    loadDefaultTemplate()
  }
})
</script>

<template>
  <div class="flex h-full bg-stone-200">
    <BaseModal
      :is-open="showTestEmailModal"
      :heading-title="t('pages.email.emailTemplateCreatePage.sendTestEmailTitle')"
      :link-text="t('pages.email.emailTemplateCreatePage.send')"
      :cancel="t('global.words.cancel')"
      :is-processing="isSendingTest"
      size="sm"
      @close="showTestEmailModal = false"
      @click="handleSendTest"
    >
      <div class="space-y-4">
        <p class="text-sm text-foreground-secondary">
          {{ t('pages.email.emailTemplateCreatePage.sendTestEmailDescription') }}
        </p>
        <div class="space-y-2">
          <label class="text-sm font-medium text-foreground">
            {{ t('pages.email.emailTemplateCreatePage.emailAddress') }}
          </label>
          <Input
            v-model="testEmailAddress"
            type="email"
            disabled
            :placeholder="t('pages.email.emailTemplateCreatePage.emailPlaceholder')"
            class="w-full"
          />
        </div>
      </div>
    </BaseModal>

    <BaseModal
      :is-open="showSaveTemplateModal"
      :heading-title="t('pages.email.emailTemplateCreatePage.saveTemplateTitle')"
      :link-text="t('pages.email.emailTemplateCreatePage.save')"
      :cancel="t('global.words.cancel')"
      :is-processing="isSavingTemplate"
      size="sm"
      :is-disabled="isSavingTemplate || saveTemplateNameInput.length === 0"
      @close="showSaveTemplateModal = false"
      @click="handleSaveModalConfirm"
    >
      <div class="space-y-4">
        <p class="text-sm text-foreground-secondary">
          {{ t('pages.email.emailTemplateCreatePage.saveTemplateDescription') }}
        </p>
        <div class="space-y-2">
          <label class="text-sm text-foreground" for="saveTemplateInputRef">
            {{ t('pages.email.emailTemplateCreatePage.templateName') }}
          </label>
          <Input
            id="saveTemplateInputRef"
            v-model="saveTemplateNameInput"
            autofocus
            :placeholder="t('pages.email.emailTemplateCreatePage.templateNamePlaceholder')"
            class="w-full mt-2"
            @keydown.enter="handleSaveModalConfirm"
          />
        </div>
      </div>
    </BaseModal>

    <EmailEditorPanel
      v-model:email-styles="emailStyles"
      :color-presets="colorPresets"
      :selected-block="selectedBlock"
      :selected-cell-index="selectedCellIndex"
      :selected-cell="selectedCell"
      :header-selected="selectedBlockId === 'header'"
      @deselect="onPanelBack"
    />

    <div class="flex flex-1 flex-col">
      <div class="flex h-[77px] items-center justify-end border-b border-border bg-white px-4">
        <div class="flex items-center gap-3">
          <div class="flex rounded-lg border border-border bg-muted p-1">
            <button
              type="button"
              class="cursor-pointer rounded-md px-3 py-1.5 text-sm transition-colors"
              :class="[
                viewMode === 'mobile'
                  ? 'bg-white text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              ]"
              @click="viewMode = 'mobile'"
            >
              <BaseIcon name="HeroDevicePhoneMobileIconOutline" class="h-4 w-4" />
            </button>
            <button
              type="button"
              class="cursor-pointer rounded-md px-3 py-1.5 text-sm transition-colors"
              :class="[
                viewMode === 'desktop'
                  ? 'bg-white text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              ]"
              @click="viewMode = 'desktop'"
            >
              <BaseIcon name="HeroComputerDesktopIconOutline" class="h-4 w-4" />
            </button>
          </div>

          <Button variant="outline" class="" @click="emit('close')">
            <span class="text-foreground">
              {{ t('global.words.back') }}
            </span>
          </Button>

          <Button variant="outline" @click="openTestEmailModal">
            {{ t('pages.email.emailTemplateCreatePage.sendTestEmail') }}
          </Button>

          <Button variant="outline" :disabled="isSaving" @click="saveTemplate">
            {{ t('pages.email.emailTemplateCreatePage.saveTemplate') }}
          </Button>

          <Button :disabled="isSaving" @click="openSaveModal">
            {{ t('pages.email.emailTemplateCreatePage.save') }}
          </Button>
        </div>
      </div>

      <div class="relative flex min-h-0 flex-1 flex-col">
        <div
          id="email-toolbar-target"
          class="flex shrink-0 justify-center border-b border-border bg-white empty:hidden"
        ></div>
        <div id="email-variable-popups"></div>
        <EmailTiptapToolbarWrapper
          :visible="activeMetadataField !== null"
          :variables="metadataVariables"
          disabled
          @insert-variable="onInsertMetadataVariable"
        />
        <div class="dotted-background relative flex-1 overflow-auto bg-content-bg py-8" @click.self="clearSelection">
          <div class="mx-auto" :class="[viewMode === 'mobile' ? 'w-[375px]' : 'w-full max-w-[600px]']">
            <div class="rounded-lg border border-border bg-white pb-2">
              <div class="space-y-3 border-b border-border p-6">
                <div class="flex items-center gap-4">
                  <span class="w-14 text-sm text-foreground-secondary">
                    {{ t('pages.email.emailTemplateCreatePage.from') }}
                  </span>
                  <p class="flex-1 text-sm text-muted-foreground">
                    {{ emailMetadata.from }}
                  </p>
                </div>

                <div class="border-t border-border pt-3">
                  <div class="flex items-center gap-4">
                    <span class="w-14 text-sm text-foreground-secondary">
                      {{ t('pages.email.emailTemplateCreatePage.subject') }}
                    </span>
                    <EmailVariableInput
                      ref="subjectFieldRef"
                      :model-value="emailMetadata.subject"
                      :maxlength="SUBJECT_MAX_LENGTH"
                      :aria-invalid="saveErrors.subject"
                      :placeholder="t('pages.email.emailTemplateCreatePage.subjectPlaceholder')"
                      @focus="onMetadataFieldFocus('subject')"
                      @update:model-value="
                        (v) => {
                          emailMetadata.subject = v
                          saveErrors.subject = false
                        }
                      "
                    />
                  </div>
                  <p v-if="saveErrors.subject" class="mt-1 pl-18 text-xs text-red-500">
                    {{ t('pages.email.emailTemplateCreatePage.subjectRequired') }}
                  </p>
                  <p class="mt-1 pl-18 text-xs" :class="isSubjectAtLimit ? 'text-red-500' : 'text-muted-foreground'">
                    {{ emailMetadata.subject.length }}/{{ SUBJECT_MAX_LENGTH }}
                  </p>
                </div>

                <div class="border-t border-border pt-3">
                  <div class="flex items-center gap-4">
                    <span class="w-14 text-sm text-foreground-secondary">
                      {{ t('pages.email.emailTemplateCreatePage.previewText') }}
                    </span>
                    <EmailVariableInput
                      ref="previewTextFieldRef"
                      :model-value="emailMetadata.previewText"
                      :maxlength="PREVIEW_TEXT_MAX_LENGTH"
                      :aria-invalid="saveErrors.previewText"
                      :placeholder="t('pages.email.emailTemplateCreatePage.previewTextPlaceholder')"
                      @focus="onMetadataFieldFocus('previewText')"
                      @update:model-value="
                        (v) => {
                          emailMetadata.previewText = v
                          saveErrors.previewText = false
                        }
                      "
                    />
                  </div>
                  <p v-if="saveErrors.previewText" class="mt-1 pl-18 text-xs text-red-500">
                    {{ t('pages.email.emailTemplateCreatePage.previewTextRequired') }}
                  </p>
                  <p
                    class="mt-1 pl-18 text-xs"
                    :class="isPreviewTextAtLimit ? 'text-red-500' : 'text-muted-foreground'"
                  >
                    {{ emailMetadata.previewText.length }}/{{ PREVIEW_TEXT_MAX_LENGTH }}
                  </p>
                </div>
              </div>

              <EmailEditorProvider :editable="true">
                <div
                  :style="{ backgroundColor: `#${emailStyles.backgroundColor}`, padding: '40px 20px' }"
                  @click.self="clearSelection"
                >
                  <div
                    :style="{
                      backgroundColor: `#${emailStyles.contentBackgroundColor}`,
                      fontFamily: emailStyles.fontFamily,
                      borderRadius: '12px'
                    }"
                    @click.self="clearSelection"
                  >
                    <EmailHeaderBlock
                      :header="emailHeader"
                      :is-selected="selectedBlockId === 'header'"
                      :is-uploading="isHeaderUploading"
                      :color-presets="colorPresets"
                      @select="selectBlock('header')"
                      @update:header="emailHeader = $event"
                      @upload-file="onHeaderUpload"
                    />

                    <draggable
                      v-model="blocks"
                      :group="{ name: 'email-blocks', pull: true, put: true }"
                      ghost-class="drop-ghost"
                      chosen-class="drag-chosen"
                      handle=".drag-handle"
                      filter="[contenteditable]"
                      :prevent-on-filter="false"
                      :animation="200"
                      @change="onDraggableChange"
                    >
                      <div v-for="block in blocks" :key="block.id" class="drag-item relative">
                        <EmailBlockActionMenu
                          v-if="selectedBlockId === block.id && block.type !== 'footer'"
                          :can-move-up="canMoveUp(block.id)"
                          :can-move-down="canMoveDown(block.id)"
                          @move-up="moveBlockUp(block.id)"
                          @move-down="moveBlockDown(block.id)"
                          @duplicate="duplicateBlock(block.id)"
                          @delete="deleteBlock(block.id)"
                        />
                        <EmailTextBlock
                          v-if="block.type === 'text'"
                          :block="block"
                          :is-selected="selectedBlockId === block.id"
                          :color-presets="colorPresets"
                          @select="selectBlock(block.id)"
                          @focus="onBlockFocus(block.id)"
                          @blur="focusedBlockId = null"
                          @update:content="block.content = $event"
                          @update:block="onBlockUpdate"
                        />

                        <EmailSpacerBlock
                          v-else-if="block.type === 'spacer'"
                          :block="block"
                          :is-selected="selectedBlockId === block.id"
                          :color-presets="colorPresets"
                          @select="selectBlock(block.id)"
                          @update:block="onBlockUpdate"
                        />

                        <div v-else-if="block.type === 'image'">
                          <EmailImageBlock
                            :block="block"
                            :is-selected="selectedBlockId === block.id"
                            :is-uploading="uploadingBlockIds.has(block.id)"
                            :is-mask-busy="masksBusy.has(block.id)"
                            :color-presets="colorPresets"
                            :class="{ 'ring-2 ring-red-500 rounded': saveErrors.image && !(block as ImageBlock).src }"
                            @select="selectBlock(block.id)"
                            @upload-file="(file) => onImageBlockUpload(block.id, file)"
                            @update:block="onBlockUpdate"
                            @mask-change="(mask) => onImageMaskChange(block.id, mask)"
                          />
                          <p
                            v-if="saveErrors.image && !(block as ImageBlock).src"
                            class="mt-1 text-center text-xs text-red-500"
                          >
                            {{ t('pages.email.emailTemplateCreatePage.imageRequired') }}
                          </p>
                        </div>

                        <EmailButtonBlock
                          v-else-if="block.type === 'button'"
                          :block="block"
                          :is-selected="selectedBlockId === block.id"
                          :color-presets="colorPresets"
                          @select="selectBlock(block.id)"
                          @update:block="onBlockUpdate"
                        />

                        <EmailSeparatorBlock
                          v-else-if="block.type === 'separator'"
                          :block="block"
                          :is-selected="selectedBlockId === block.id"
                          :color-presets="colorPresets"
                          @select="selectBlock(block.id)"
                          @update:block="onBlockUpdate"
                        />

                        <EmailGalleryBlock
                          v-else-if="block.type === 'gallery'"
                          :block="block"
                          :is-selected="selectedBlockId === block.id"
                          :selected-cell-index="selectedBlockId === block.id ? selectedCellIndex : null"
                          :uploading-item-ids="uploadingGalleryItems.get(block.id)"
                          :color-presets="colorPresets"
                          @select="selectBlock(block.id)"
                          @update:selected-cell-index="selectedCellIndex = $event"
                          @upload-file="(payload) => onGalleryItemUpload(block.id, payload)"
                          @update:block="onBlockUpdate"
                        />

                        <EmailFooterBlock
                          v-else-if="block.type === 'footer'"
                          :block="block"
                          :is-selected="selectedBlockId === block.id"
                          :color-presets="colorPresets"
                          @select="selectBlock(block.id)"
                          @update:block="onBlockUpdate"
                          @update:content="block.content = $event"
                        />

                        <EmailSocialBlock
                          v-else-if="block.type === 'social'"
                          :block="block as SocialBlock"
                          :is-selected="selectedBlockId === block.id"
                          :color-presets="colorPresets"
                          @select="selectBlock(block.id)"
                          @update:block="onBlockUpdate"
                        />
                      </div>
                    </draggable>
                  </div>
                </div>
              </EmailEditorProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
@reference "@/input.css";

.drag-chosen :deep(.email-block) {
  @apply !border-foreground !bg-muted;
}
/* Only here because Raphaël can't live without centered text */
.text-block-editor[style*='text-align: center'] .is-editor-empty::before {
  float: none !important;
}
</style>
